import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type {
  ContactAttempt,
  ContactedNotReplied,
  DiscoveryFolderSummary,
  DiscoveryProspect,
  DiscoveryProspectStatus,
  OutreachInvite,
} from '@/api/types';

export const discoveryAssignedCountKey = ['portal', 'discovery-assigned-count'] as const;
export const discoveryFoldersKey = ['portal', 'discovery-folders'] as const;
export const discoveryQueueKey = (filter: {
  status?: DiscoveryProspectStatus;
  claimed?: 'mine' | 'any';
  folderPublicId?: string | null;
}) => ['portal', 'discovery-prospects', filter] as const;

export interface AssignedCounts {
  direct: number;
  team: number;
}

export function useAssignedCounts() {
  const api = useApi();
  return useQuery({
    queryKey: discoveryAssignedCountKey,
    queryFn: () => api.get<AssignedCounts>('/portal/prospects/assigned-count'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Folder-grouped summary — the outreacher's main entry point. Refetched on
 * an interval so newly-exported folders show up without a manual reload.
 */
export function useDiscoveryFolders() {
  const api = useApi();
  return useQuery({
    queryKey: discoveryFoldersKey,
    queryFn: () => api.get<DiscoveryFolderSummary[]>('/portal/prospects/folders'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Every lead visible to the caller, across every folder, a page at a time.
 *
 * The queue opens on one worklist rather than a folder grid, so this lists
 * without `folderPublicId`: the backend applies the same visibility rule
 * (assigned to me, to my team, or to nobody) and the same default statuses
 * (AVAILABLE + CONTACTED) as a per-folder list. Folder, tab and filters are
 * applied in the browser over the whole set, so every count on screen is
 * honest against everything loaded rather than one page.
 *
 * `limit` is the server's own cap; asking for fewer per page only means more
 * round trips. The walk is self-driving: each landed page re-runs the effect
 * until `getNextPageParam` says there is nothing left.
 *
 * `status: 'PROMOTED'` lists logged leads instead, for the Done tab.
 * `status: 'CONTACTED'` lists only messaged ones, for matching My creators'
 * Messaged rows back to their queue lead.
 */
const QUEUE_PAGE_SIZE = 200;

export function useQueueLeads(
  status?: Extract<DiscoveryProspectStatus, 'PROMOTED' | 'CONTACTED'>,
  enabled = true,
) {
  const api = useApi();
  const query = useInfiniteQuery({
    queryKey: [...discoveryQueueKey({ status }), 'worklist'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.getPaged<DiscoveryProspect[]>('/portal/prospects', {
        status,
        page: pageParam,
        limit: QUEUE_PAGE_SIZE,
      }),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.data.length, 0);
      return loaded < (last.meta?.total ?? 0) && last.data.length > 0 ? all.length + 1 : undefined;
    },
    staleTime: 15_000,
    enabled,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const leads = useMemo(() => (query.data?.pages ?? []).flatMap((p) => p.data), [query.data]);
  return {
    leads,
    total: query.data?.pages[0]?.meta?.total ?? leads.length,
    /** First page still on its way. */
    isLoading: query.isLoading,
    /** Rows are on screen but more pages are still arriving. */
    stillLoading: hasNextPage === true || isFetchingNextPage,
    isError: query.isError,
    error: query.error,
  };
}

/** One messaged-not-replied record of the caller's, keyed for lookup. */
export interface MyMessagedRecord {
  firstContactedAt: string;
  lastContactedAt: string;
  attemptCount: number;
}

/**
 * When the caller messaged each creator they're still waiting on.
 *
 * Queue rows carry who messaged but not when, so the "Messaged 2d ago" line
 * comes from the caller's own messaged-not-replied list (the same rows as My
 * creators' Messaged tab), matched on channel + handle.
 */
export function useMyMessagedTimes(enabled = true): Map<string, MyMessagedRecord> {
  const api = useApi();
  const query = useInfiniteQuery({
    queryKey: ['invites', 'contacted', 'all'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.getPaged<ContactedNotReplied[]>('/portal/invites', {
        status: 'CONTACTED',
        page: pageParam,
        limit: QUEUE_PAGE_SIZE,
      }),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.data.length, 0);
      return loaded < (last.meta?.total ?? 0) && last.data.length > 0 ? all.length + 1 : undefined;
    },
    staleTime: 15_000,
    enabled,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return useMemo(() => {
    const out = new Map<string, MyMessagedRecord>();
    for (const page of query.data?.pages ?? []) {
      for (const r of page.data) {
        out.set(handleKey(r.channel, r.targetIdentifier), {
          firstContactedAt: r.firstContactedAt,
          lastContactedAt: r.lastContactedAt,
          attemptCount: r.attemptCount,
        });
      }
    }
    return out;
  }, [query.data]);
}

function invalidateQueue(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['portal', 'discovery-prospects'] });
  qc.invalidateQueries({ queryKey: discoveryFoldersKey });
  // Creator lookup shows the same messaged marks, keyed on (channel, handle).
  qc.invalidateQueries({ queryKey: ['portal', 'outreach-history'] });
  // My creators' Messaged tab, and the "Messaged 2d ago" times on queue rows.
  qc.invalidateQueries({ queryKey: ['invites'] });
}

export interface PromoteInput {
  publicId: string;
  category: string;
  niches?: string[];
  notes?: string | null;
  /** Optional screenshot attachment — recommended for payout audit. */
  screenshot?: File;
}

export interface PromoteResponse {
  prospectPublicId: string;
  invite: OutreachInvite;
}

/**
 * Log reply: the creator replied asking how to join. Creates a real
 * outreach_invite (attributed to the caller via Clerk), optionally attaches a
 * screenshot, and removes the prospect from the queue. Two outreachers racing on the same target is
 * safe: the unique constraint on (tenant, channel, target) protects the
 * data; the loser gets a 409 with a clear message.
 */
export function usePromoteProspect() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PromoteInput) => {
      const form = new FormData();
      form.append('category', input.category);
      if (input.niches) form.append('niches', JSON.stringify(input.niches));
      if (input.notes !== undefined && input.notes !== null) form.append('notes', input.notes);
      if (input.screenshot) form.append('file', input.screenshot);
      return api.postForm<PromoteResponse>(
        `/portal/prospects/${encodeURIComponent(input.publicId)}/promote`,
        form,
      );
    },
    onSuccess: () => {
      invalidateQueue(qc);
      qc.invalidateQueries({ queryKey: ['portal', 'invites'] });
    },
  });
}

export interface ContactInput {
  publicId: string;
  note?: string | null;
}

/**
 * "I messaged them": record that the caller has DM'd this creator.
 *
 * Unpaid and non-exclusive — no invite is created, nothing becomes owed, and
 * the prospect stays in every teammate's queue. It exists so the team can see
 * who has already messaged someone before deciding whether to message them
 * too. The invite (and the log bonus) still happens later, when the creator
 * replies.
 */
export function useMarkContacted() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContactInput) =>
      api.post<{ attempts: ContactAttempt[] }>(
        `/portal/prospects/${encodeURIComponent(input.publicId)}/contact`,
        { note: input.note ?? null },
      ),
    onSuccess: () => invalidateQueue(qc),
  });
}

/**
 * "Skip (wrong fit)": mark a lead as not relevant to its folder, for the
 * caller only.
 *
 * Posting again updates the note rather than adding a second mark. Teammates
 * are unaffected: the lead stays in their queue at the same status.
 */
export function useMarkIrrelevant() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { publicId: string; note?: string | null }) =>
      api.post<unknown>(`/portal/prospects/${encodeURIComponent(input.publicId)}/irrelevant`, {
        note: input.note ?? null,
      }),
    onSuccess: () => invalidateQueue(qc),
  });
}

/** "Unskip": undo the caller's own skip (irrelevant) mark. */
export function useUnmarkIrrelevant() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) =>
      api.del<unknown>(`/portal/prospects/${encodeURIComponent(publicId)}/irrelevant`),
    onSuccess: () => invalidateQueue(qc),
  });
}

/** Undo the caller's own messaged mark. Never touches anyone else's. */
export function useUnmarkContacted() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) =>
      api.del<{ attempts: ContactAttempt[] }>(
        `/portal/prospects/${encodeURIComponent(publicId)}/contact`,
      ),
    onSuccess: () => invalidateQueue(qc),
  });
}

/**
 * Helper for one-off "I'm not going to work this" actions. Kept for parity
 * with the original claim-flow API, but the UI no longer surfaces it as a
 * primary action — the simpler "mark as invited or skip" model is the
 * default.
 */
export function useDismissProspect() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, reason }: { publicId: string; reason?: string | null }) =>
      api.post<DiscoveryProspect>(
        `/portal/prospects/${encodeURIComponent(publicId)}/dismiss`,
        { reason: reason ?? null },
      ),
    onSuccess: () => invalidateQueue(qc),
  });
}

/**
 * Pulls a single readable field out of the enrichment blob.
 */
export function enrichmentField(
  prospect: DiscoveryProspect,
  key: 'followers' | 'avgViews' | 'email' | 'nickname' | 'bio' | 'profileUrl',
): string | number | null {
  const v = prospect.enrichmentSnapshot?.[key];
  if (typeof v === 'string' || typeof v === 'number') return v;
  return null;
}

// ── Pure helpers (tested in src/lib/__tests__/queue-copy.test.ts) ─────────

/**
 * Split a folder card's counts into "nobody's messaged" and "messaged".
 *
 * `availableCount` is every workable lead (open plus messaged, since
 * messaging reserves nothing) and `contactedCount` is the messaged subset,
 * so the unmessaged number is the difference. Clamped so a stale or
 * inconsistent summary can never render a negative count.
 */
export function folderLeadCounts(
  folder: Pick<DiscoveryFolderSummary, 'availableCount' | 'contactedCount'>,
): { unmessaged: number; messaged: number } {
  const available = Math.max(0, folder.availableCount);
  const messaged = Math.min(available, Math.max(0, folder.contactedCount));
  return { unmessaged: available - messaged, messaged };
}

/**
 * Names for the "who messaged this creator" line, with the caller shown as
 * "You" and listed first so the sentence reads "You and Sam messaged...".
 */
export function messagedNames(
  contactedBy: ReadonlyArray<{ inviterId: number; inviterName: string }>,
  myInviterId: number | null,
): { names: string[]; includesMe: boolean } {
  const mine = contactedBy.filter((c) => myInviterId !== null && c.inviterId === myInviterId);
  const others = contactedBy.filter((c) => myInviterId === null || c.inviterId !== myInviterId);
  const includesMe = mine.length > 0;
  return {
    names: [...(includesMe ? ['You'] : []), ...others.map((c) => c.inviterName)],
    includesMe,
  };
}

/**
 * Turn a lead-queue API error into copy an outreacher can act on. Known
 * backend codes get our own wording (the raw messages use old vocabulary);
 * anything else falls back to the server's message.
 */
export function queueActionError(err: unknown): string {
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? (err as { code: unknown }).code
      : null;
  switch (code) {
    case 'CONTACT_WITHDRAW_FAILED':
      return "Nothing to undo. You haven't marked this creator messaged, or their reply is already logged.";
    case 'PROSPECT_DISMISSED':
      return 'An admin removed this lead, so it can no longer be changed.';
    case 'PROSPECT_ALREADY_PROMOTED':
      return "Someone already logged this creator's reply.";
    case 'PROSPECT_NOT_FOUND':
      return 'This lead no longer exists. An admin may have removed it.';
    default:
      break;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong. Try again.';
}

// ── Worklist helpers (tested in src/lib/__tests__/queue-copy.test.ts) ─────

/**
 * The queue's three tabs follow the flow: Found → Messaged → Replied.
 *
 *   - To message: nobody's word from you yet (teammates may have messaged).
 *   - Messaged · waiting: you messaged them; log the reply when it comes.
 *   - Done: their reply was logged, or you skipped them as a wrong fit.
 */
export type QueueTab = 'message' | 'waiting' | 'done';

export const QUEUE_TABS: ReadonlyArray<{ key: QueueTab; label: string }> = [
  { key: 'message', label: 'To message' },
  { key: 'waiting', label: 'Messaged · waiting' },
  { key: 'done', label: 'Done' },
];

export function resolveQueueTab(raw: string | null | undefined): QueueTab {
  return QUEUE_TABS.some((t) => t.key === raw) ? (raw as QueueTab) : 'message';
}

/**
 * The one line that replaces the captions every row used to repeat. Says the
 * two things outreachers get wrong: messaging pays nothing, and the log
 * waits for a reply asking how to join.
 */
export const QUEUE_LEGEND =
  '"I messaged them" isn\'t a log and earns nothing. Log a reply once they ask how to join.';

/**
 * Which tab a lead belongs in, for the calling outreacher. Null means it
 * isn't on their worklist at all (removed by an admin, or logged by someone
 * else on a lead they never touched).
 *
 * A logged lead counts as Done only when the caller messaged or skipped it:
 * the list endpoint doesn't say who logged the reply, so "touched it" is the
 * closest honest proxy for "this was yours".
 */
export function queueTabFor(
  p: Pick<DiscoveryProspect, 'status' | 'contactedByMe' | 'flaggedByMe'>,
): QueueTab | null {
  if (p.status === 'DISMISSED') return null;
  if (p.status === 'PROMOTED') return p.contactedByMe || p.flaggedByMe ? 'done' : null;
  if (p.flaggedByMe) return 'done';
  if (p.contactedByMe) return 'waiting';
  return 'message';
}

/** Where a lead sits on the Found ▸ Messaged ▸ Replied strip. */
export type LeadStep = 'found' | 'messaged' | 'replied';

export function leadStep(p: Pick<DiscoveryProspect, 'status' | 'contactedByMe'>): LeadStep {
  if (p.status === 'PROMOTED') return 'replied';
  if (p.contactedByMe) return 'messaged';
  return 'found';
}

/**
 * Lookup key for a creator: channel plus the handle as the backend compares
 * it (case-insensitive, leading @ ignored).
 */
export function handleKey(channel: string, handle: string): string {
  return `${channel}:${handle.trim().replace(/^@+/, '').toLowerCase()}`;
}

/** "Messaged today" / "Messaged yesterday" / "Messaged 3d ago". */
export function messagedAgo(ts: string | null | undefined, now: Date = new Date()): string | null {
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return null;
  const days = Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
  if (days === 0) return 'Messaged today';
  if (days === 1) return 'Messaged yesterday';
  return `Messaged ${days}d ago`;
}

/** Stable key for a folder chip; the no-folder bucket gets its own. */
export const NO_FOLDER_KEY = 'unfiled';
export function folderKey(id: string | null | undefined): string {
  return id ?? NO_FOLDER_KEY;
}

/**
 * Briefing strips open the first time someone sees a folder and stay shut
 * after that, unless they open one themselves. `stored` is what the browser
 * remembered for this folder ('open' / 'closed'), or undefined for a folder
 * they've never seen.
 */
export function briefingStartsOpen(stored: string | null | undefined): boolean {
  return stored !== 'closed';
}

/** Whether a folder has anything to brief: campaigns, best campaigns or pitch notes. */
export function hasBriefing(
  f: Pick<DiscoveryFolderSummary, 'recommendedCampaigns' | 'pitchNotes' | 'campaigns'> | null | undefined,
): boolean {
  if (!f) return false;
  return Boolean(f.recommendedCampaigns?.trim() || f.pitchNotes?.trim() || (f.campaigns ?? []).length > 0);
}

/**
 * Short "who messaged" for a row's muted line: "Sam messaged",
 * "You and Sam messaged", "Sam and 2 others messaged". `names` comes from
 * {@link messagedNames}, so the caller is already "You" and first.
 */
export function whoMessaged(names: ReadonlyArray<string>): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return `${names[0]} messaged`;
  if (names.length === 2) return `${names[0]} and ${names[1]} messaged`;
  return `${names[0]} and ${names.length - 1} others messaged`;
}
